import { useEffect, useMemo, useRef, useState } from 'react'
import { ExitButton } from '../components/ExitButton'
import type { CSSProperties, MutableRefObject } from 'react'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { api, type Universe } from '../api'
import { font, radius, TAP_MIN } from '../theme'
import { ErrorScreen, Loading } from '../components/States'
import { Mascot } from '../components/Mascot'
import { Trace } from '../lib/trace'
import { say } from '../lib/speech'
import { sfx } from '../lib/sound'

/**
 * The word universe in 3D: a child's words as a little solar system they can
 * fly around.
 *
 * A parent asked for the universe to feel like Google Earth, or like flying
 * from planet to planet, because the flat sky felt basic. The flat version
 * (WordUniverse.tsx) stays: it is lighter, it works without a graphics chip,
 * and this screen links to it. Both draw the same facts.
 *
 * **The picture.** The sun in the middle is the child's whole collection.
 * Each level is a planet on its own orbit, nearest the sun the easiest, and
 * its size grows with how many words it holds. The words themselves are the
 * stars in a ring round their planet, like Saturn's. A fully grown word is a
 * warm gold star with a glow; a word still growing is smaller and pale blue;
 * a word met in the last week swells gently. Grown words sit on the inside of
 * the ring, so every planet has a bright heart.
 *
 * **It only grows** (D-048). Nothing here dims or goes out. The "pulse" on a
 * recent word only ever gets brighter and bigger than its resting state, never
 * fainter, and a grown word stays gold even if it was later missed.
 *
 * **Moving about.** Drag spins the system, the wheel or a pinch zooms. There
 * is no panning on purpose: a child who can pan can lose the sun off the edge
 * and not know how to get it back. Travel is by tapping instead: tap a planet
 * (or its name, or its chip at the bottom) and the camera flies there in about
 * a second, then rides along with the planet as it orbits, so the child can
 * spin round it like a globe. "Whole system" flies back out.
 *
 * **Tapping a word.** A star is far smaller than a finger, so a tap takes the
 * nearest star on screen within a finger's reach, rather than asking the
 * graphics card which pixel was hit. When you zoom in on a planet, the words
 * nearest you float up as little name tags, and those can be tapped too. The
 * tapped word is spoken, and its meaning comes from the word map, exactly as
 * on the flat sky. Its friends (words it is joined to) are drawn as lines and
 * offered as chips, so one word leads to the next.
 *
 * **Performance.** About 1,100 words is ten point clouds, one per planet,
 * drawn by one small shader, not a thousand meshes. Labels are plain HTML
 * placed over the canvas each frame, so they stay sharp and readable.
 *
 * **Calm.** With "reduce motion" switched on, nothing orbits, spins or
 * pulses, and the camera jumps rather than flies.
 */

type Star = { w: string; g: number; b: number | null; d: number | null; planet: number; local: THREE.Vector3 }
type Planet = {
  band: number | null; name: string; hue: string; count: number; grown: number
  r: number; ringIn: number; ringOut: number; orbit: number; phase: number; speed: number; spin: number
  tilt: THREE.Euler; stars: number[]
}
type System = { planets: Planet[]; stars: Star[]; extent: number }

// The same colours as the flat sky, so a level is the same colour in both.
const HUES = ['#7C5CFF', '#FF6FB5', '#4FC3F7', '#5EEAD4', '#FFB347', '#A3E635',
              '#F472B6', '#60A5FA', '#FBBF24', '#34D399', '#C084FC', '#F87171', '#22D3EE']
const GOLDEN = Math.PI * (3 - Math.sqrt(5))
const SUN_R = 7
/** Words per square unit of ring: tight enough to read as a ring, loose enough to tap. */
const DENSITY = 1.5
/** Space between one planet's ring and the next. */
const GAP = 3.5
const FOV = 50
/** How far, in screen pixels, a tap can land from a star and still pick it. */
const REACH = 26
const WORD_LABELS = 10
/** How high above the plane the whole system is seen from (radians). A tall
 *  phone looks down more steeply, so the round system fills more of a tall
 *  screen instead of being a thin oval with empty sky above and below. */
const overviewEl = (aspect: number) => (aspect < 0.8 ? 1.2 : 0.95)

/** A fixed random sequence, so the system is the same every visit. */
function seeded(seed: number) {
  let s = seed >>> 0
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0
    return s / 2 ** 32
  }
}

function layout(data: Universe): System {
  const groups = new Map<number | null, Universe['stars']>()
  for (const s of data.stars) {
    const key = s.b ?? null
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(s)
  }
  const keys = [...groups.keys()].sort((a, b) => (a ?? 99) - (b ?? 99))
  const names = new Map(data.galaxies.map((g) => [g.band, g.name]))
  const rnd = seeded(11)

  const planets: Planet[] = []
  const stars: Star[] = []
  let orbit = SUN_R + 10
  let prevOut = 0
  keys.forEach((band, k) => {
    const members = [...groups.get(band)!].sort((a, b) => b.g - a.g || a.w.localeCompare(b.w))
    const n = members.length
    // Size by word count, but gently (square root), so a level with 8 words
    // is still a planet you can see next to one with 260.
    const r = 1.8 + 0.2 * Math.sqrt(n)
    const ringIn = r + 1.5
    const ringOut = Math.sqrt(ringIn ** 2 + n / (Math.PI * DENSITY))
    // Each orbit clears the last planet's ring and this one's, so two
    // planets can never pass through each other however they line up.
    orbit += k === 0 ? ringOut : prevOut + ringOut + GAP
    prevOut = ringOut
    const tilt = new THREE.Euler(0.22 + rnd() * 0.4, 0, (rnd() - 0.5) * 0.5)
    const index = planets.length
    const own: number[] = []
    members.forEach((s, i) => {
      // Even spread over the ring's area (hence the square root), turning by
      // the golden angle so no two words line up in spokes.
      const rr = Math.sqrt(ringIn ** 2 + (ringOut ** 2 - ringIn ** 2) * ((i + 0.5) / n))
      const th = i * GOLDEN + k
      const y = (rnd() - 0.5) * (0.5 + 0.03 * Math.sqrt(n))
      const local = new THREE.Vector3(Math.cos(th) * rr, y, Math.sin(th) * rr).applyEuler(tilt)
      own.push(stars.length)
      stars.push({ w: s.w, g: s.g, b: s.b, d: s.d, planet: index, local })
    })
    planets.push({
      band, name: band === null ? 'Unsorted' : names.get(band) ?? `Level ${band}`,
      hue: HUES[(band ?? 12) % HUES.length]!, count: n, grown: members.filter((s) => s.g).length,
      r, ringIn, ringOut, orbit,
      phase: k * 2.399 + 0.6,
      // Inner planets go round faster, as real ones do, but not so steeply
      // that the outer ones look frozen.
      speed: 1.2 / Math.pow(orbit, 0.9),
      spin: 0.12 + rnd() * 0.15,
      tilt, stars: own,
    })
  })
  const last = planets[planets.length - 1]
  return { planets, stars, extent: last ? last.orbit + last.ringOut : SUN_R * 5 }
}

function hexRGB(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16)
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255]
}

let styled = false
function ensureStyle(): void {
  if (styled || typeof document === 'undefined') return
  styled = true
  const el = document.createElement('style')
  el.textContent = `
    .ws3-scroll { scrollbar-width: none; }
    .ws3-scroll::-webkit-scrollbar { display: none; }
    .ws3-input::placeholder { color: rgba(220,215,255,0.65); }
    @keyframes ws3-in { from { opacity: 0; transform: translateY(12px) } to { opacity: 1; transform: none } }
    .ws3-in { animation: ws3-in 0.28s ease-out both; }
    @media (prefers-reduced-motion: reduce) { .ws3-in { animation: none } }
  `
  document.head.appendChild(el)
}

// ------------------------------------------------------------------ scene

interface Labels {
  planets: (HTMLButtonElement | null)[]
  sun: HTMLButtonElement | null
  words: (HTMLButtonElement | null)[]
  picked: HTMLDivElement | null
  hover: HTMLDivElement | null
  /** The bars at the top and bottom: no name is placed underneath them. */
  reserved: (HTMLElement | null)[]
}
interface Handlers {
  tapStar: (i: number) => void
  tapPlanet: (i: number) => void
  tapSun: () => void
  tapEmpty: () => void
  focus: (i: number | null) => void
  acted: () => void
}
interface SceneApi {
  flyToPlanet: (i: number) => void
  flyHome: () => void
  flyToStar: (i: number, close: boolean) => void
  select: (i: number | null) => void
  setLinks: (ids: number[]) => void
  dispose: () => void
}

function canvasTexture(w: number, h: number, draw: (g: CanvasRenderingContext2D) => void): THREE.CanvasTexture {
  const c = document.createElement('canvas')
  c.width = w
  c.height = h
  const g = c.getContext('2d')
  if (g) draw(g)
  const t = new THREE.CanvasTexture(c)
  t.colorSpace = THREE.SRGBColorSpace
  return t
}

/** A soft white blob, tinted by whatever uses it: glows, haloes, nebulae. */
function glowTexture() {
  return canvasTexture(128, 128, (g) => {
    const grad = g.createRadialGradient(64, 64, 0, 64, 64, 64)
    grad.addColorStop(0, 'rgba(255,255,255,1)')
    grad.addColorStop(0.25, 'rgba(255,255,255,0.55)')
    grad.addColorStop(0.6, 'rgba(255,255,255,0.12)')
    grad.addColorStop(1, 'rgba(255,255,255,0)')
    g.fillStyle = grad
    g.fillRect(0, 0, 128, 128)
  })
}

/** A banded, gas-giant look in the planet's colour. Seeded, so it never changes. */
function planetTexture(hue: string, seed: number) {
  const rnd = seeded(seed)
  const base = new THREE.Color(hue)
  return canvasTexture(256, 128, (g) => {
    g.fillStyle = hue
    g.fillRect(0, 0, 256, 128)
    const bands = 8 + Math.floor(rnd() * 5)
    for (let i = 0; i < bands; i++) {
      const y = rnd() * 128
      const h = 3 + rnd() * 14
      g.fillStyle = base.clone().offsetHSL((rnd() - 0.5) * 0.06, (rnd() - 0.5) * 0.2, (rnd() - 0.5) * 0.42).getStyle()
      g.globalAlpha = 0.55 + rnd() * 0.4
      g.beginPath()
      g.moveTo(0, y)
      for (let x = 0; x <= 256; x += 16) g.lineTo(x, y + Math.sin(x / 38 + i) * 2.5)
      for (let x = 256; x >= 0; x -= 16) g.lineTo(x, y + h + Math.sin(x / 29 + i * 2) * 2.5)
      g.closePath()
      g.fill()
    }
    for (let i = 0; i < 3; i++) {
      const x = rnd() * 256
      const y = 30 + rnd() * 68
      const rr = 6 + rnd() * 10
      const grad = g.createRadialGradient(x, y, 0, x, y, rr)
      grad.addColorStop(0, 'rgba(255,255,255,0.55)')
      grad.addColorStop(1, 'rgba(255,255,255,0)')
      g.globalAlpha = 1
      g.fillStyle = grad
      g.beginPath()
      g.ellipse(x, y, rr * 1.6, rr, 0, 0, Math.PI * 2)
      g.fill()
    }
    g.globalAlpha = 1
  })
}

function sunTexture() {
  const rnd = seeded(3)
  return canvasTexture(256, 128, (g) => {
    g.fillStyle = '#FFC93C'
    g.fillRect(0, 0, 256, 128)
    for (let i = 0; i < 160; i++) {
      const x = rnd() * 256
      const y = rnd() * 128
      const rr = 3 + rnd() * 12
      g.globalAlpha = 0.15 + rnd() * 0.25
      g.fillStyle = rnd() > 0.5 ? '#FFF1A8' : '#FF9F2E'
      g.beginPath()
      g.arc(x, y, rr, 0, Math.PI * 2)
      g.fill()
    }
    g.globalAlpha = 1
  })
}

/** The ring that marks the chosen word. */
function ringTexture() {
  return canvasTexture(128, 128, (g) => {
    g.strokeStyle = 'rgba(255,255,255,0.95)'
    g.lineWidth = 7
    g.beginPath()
    g.arc(64, 64, 44, 0, Math.PI * 2)
    g.stroke()
    g.fillStyle = 'rgba(255,255,255,0.95)'
    for (let k = 0; k < 4; k++) {
      const a = (k * Math.PI) / 2
      g.beginPath()
      g.arc(64 + Math.cos(a) * 58, 64 + Math.sin(a) * 58, 5, 0, Math.PI * 2)
      g.fill()
    }
  })
}

const WORD_VERT = `
  attribute float aSize;
  attribute vec3 aColor;
  attribute float aRecent;
  attribute float aPhase;
  uniform float uTime;
  uniform float uScale;
  uniform float uPR;
  uniform float uPulse;
  varying vec3 vColor;
  void main() {
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    // Recent words swell above their resting size and back. Never below it:
    // nothing in the universe dims (D-048).
    float p = aRecent * uPulse * (0.5 + 0.5 * sin(uTime * 2.6 + aPhase));
    float s = aSize * (1.0 + 0.55 * p);
    gl_PointSize = clamp(s * uScale / -mv.z, (2.0 + aSize * 2.8) * uPR, 56.0 * uPR);
    vColor = aColor * (1.0 + 0.6 * p);
    gl_Position = projectionMatrix * mv;
  }
`
const WORD_FRAG = `
  varying vec3 vColor;
  void main() {
    float d = length(gl_PointCoord - 0.5) * 2.0;
    if (d > 1.0) discard;
    float core = smoothstep(0.42, 0.0, d);
    float halo = exp(-d * d * 5.0) * 0.6;
    float a = core + halo;
    gl_FragColor = vec4(vColor * a + vec3(core * 0.45), a);
  }
`
const SKY_VERT = `
  attribute float aSize;
  attribute vec3 aColor;
  attribute float aPhase;
  uniform float uTime;
  uniform float uPR;
  uniform float uTwinkle;
  varying vec3 vColor;
  void main() {
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    float tw = 1.0 - uTwinkle * 0.45 * (0.5 + 0.5 * sin(uTime * (0.6 + aPhase * 0.25) + aPhase * 6.0));
    gl_PointSize = aSize * uPR;
    vColor = aColor * tw;
    gl_Position = projectionMatrix * mv;
  }
`
const SKY_FRAG = `
  varying vec3 vColor;
  void main() {
    float d = length(gl_PointCoord - 0.5) * 2.0;
    if (d > 1.0) discard;
    float a = smoothstep(1.0, 0.0, d);
    gl_FragColor = vec4(vColor * a, a);
  }
`

const RIM_VERT = `
  varying vec3 vN;
  varying vec3 vV;
  void main() {
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vN = normalize(normalMatrix * normal);
    vV = normalize(-mv.xyz);
    gl_Position = projectionMatrix * mv;
  }
`
const RIM_FRAG = `
  uniform vec3 uColor;
  varying vec3 vN;
  varying vec3 vV;
  void main() {
    float f = pow(1.0 - max(dot(vN, vV), 0.0), 2.5) * 0.85;
    gl_FragColor = vec4(uColor * f, f);
  }
`

const ease = (k: number) => (k < 0.5 ? 4 * k * k * k : 1 - (-2 * k + 2) ** 3 / 2)

function buildScene(
  mount: HTMLDivElement,
  sys: System,
  labels: MutableRefObject<Labels>,
  slots: MutableRefObject<(number | null)[]>,
  handlers: MutableRefObject<Handlers>,
  reduce: boolean,
): SceneApi {
  // Throws if the device cannot do WebGL; the caller turns that into a kind
  // message rather than a crash.
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' })
  const disposables: { dispose: () => void }[] = []
  const track = <T extends { dispose: () => void }>(x: T): T => { disposables.push(x); return x }

  const pr = Math.min(window.devicePixelRatio || 1, 2)
  renderer.setPixelRatio(pr)
  renderer.setClearColor(0x000000, 0)
  const canvas = renderer.domElement
  canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block;touch-action:none;cursor:grab;outline:none'
  canvas.setAttribute('aria-label', `${sys.stars.length} words as stars round ${sys.planets.length} planets. Drag to spin, pinch to zoom, tap to explore.`)
  canvas.setAttribute('role', 'img')
  mount.prepend(canvas)

  const scene = new THREE.Scene()
  const camera = new THREE.PerspectiveCamera(FOV, 1, 0.1, 9000)
  const controls = new OrbitControls(camera, canvas)
  controls.enableDamping = true
  controls.dampingFactor = 0.08
  controls.enablePan = false
  controls.rotateSpeed = 0.6
  controls.zoomSpeed = 0.9
  controls.minPolarAngle = 0.06
  controls.maxPolarAngle = Math.PI - 0.06

  scene.add(new THREE.AmbientLight(0x9aa6ff, 0.5))
  const sunLight = new THREE.PointLight(0xfff0d0, 3.4, 0, 0)
  scene.add(sunLight)

  const glow = track(glowTexture())

  // ------------------------------------------------ the far sky
  // It travels with the camera, so you can never fly out past it.
  const sky = new THREE.Group()
  scene.add(sky)
  {
    const N = 2200
    const rnd = seeded(5)
    const pos = new Float32Array(N * 3)
    const col = new Float32Array(N * 3)
    const size = new Float32Array(N)
    const phase = new Float32Array(N)
    const tints: [number, number, number][] = [[1, 1, 1], [0.8, 0.88, 1], [1, 0.9, 0.8], [0.92, 0.85, 1]]
    for (let i = 0; i < N; i++) {
      const u = rnd() * 2 - 1
      const th = rnd() * Math.PI * 2
      const rr = 2600 + rnd() * 1800
      const s = Math.sqrt(1 - u * u)
      pos.set([Math.cos(th) * s * rr, u * rr, Math.sin(th) * s * rr], i * 3)
      const t = tints[Math.floor(rnd() * tints.length)]!
      const b = 0.45 + rnd() * 0.55
      col.set([t[0] * b, t[1] * b, t[2] * b], i * 3)
      size[i] = rnd() < 0.06 ? 3.2 + rnd() * 1.6 : 1.1 + rnd() * 1.6
      phase[i] = rnd() * 6.28
    }
    const geo = track(new THREE.BufferGeometry())
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3))
    geo.setAttribute('aColor', new THREE.BufferAttribute(col, 3))
    geo.setAttribute('aSize', new THREE.BufferAttribute(size, 1))
    geo.setAttribute('aPhase', new THREE.BufferAttribute(phase, 1))
    const mat = track(new THREE.ShaderMaterial({
      vertexShader: SKY_VERT, fragmentShader: SKY_FRAG, transparent: true, depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: { uTime: { value: 0 }, uPR: { value: pr }, uTwinkle: { value: reduce ? 0 : 1 } },
    }))
    sky.add(new THREE.Points(geo, mat))
    // A few faint coloured clouds far away, so space is not just black.
    const nebulae: [string, number][] = [['#7C5CFF', 0.22], ['#FF6FB5', 0.14], ['#22D3EE', 0.12], ['#C084FC', 0.16], ['#4FC3F7', 0.1]]
    nebulae.forEach(([c, o], i) => {
      const m = track(new THREE.SpriteMaterial({
        map: glow, color: c, transparent: true, opacity: o, depthWrite: false, blending: THREE.AdditiveBlending,
      }))
      const sp = new THREE.Sprite(m)
      const a = i * 2.2 + 0.4
      const e = (seeded(i + 40)() - 0.5) * 1.4
      sp.position.set(Math.cos(a) * Math.cos(e) * 2400, Math.sin(e) * 2400, Math.sin(a) * Math.cos(e) * 2400)
      sp.scale.setScalar(2200 + i * 300)
      sky.add(sp)
    })
  }

  // ------------------------------------------------ the sun
  const sun = new THREE.Mesh(
    track(new THREE.SphereGeometry(SUN_R, 48, 32)),
    track(new THREE.MeshBasicMaterial({ map: track(sunTexture()) })),
  )
  scene.add(sun)
  const corona: THREE.Sprite[] = []
  ;([['#FFE08A', SUN_R * 4.2, 0.9], ['#FF9F43', SUN_R * 11, 0.32]] as [string, number, number][]).forEach(([c, s, o]) => {
    const sp = new THREE.Sprite(track(new THREE.SpriteMaterial({
      map: glow, color: c, transparent: true, opacity: o, depthWrite: false, blending: THREE.AdditiveBlending,
    })))
    sp.scale.setScalar(s)
    corona.push(sp)
    scene.add(sp)
  })

  // ------------------------------------------------ planets and their words
  const wordMat = track(new THREE.ShaderMaterial({
    vertexShader: WORD_VERT, fragmentShader: WORD_FRAG, transparent: true, depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: { uTime: { value: 0 }, uScale: { value: 1 }, uPR: { value: pr }, uPulse: { value: reduce ? 0 : 1 } },
  }))
  const GOLD = hexRGB('#FFC955')
  const BLUE = hexRGB('#A9CCFF')
  const groups: THREE.Group[] = []
  const bodies: THREE.Mesh[] = []
  sys.planets.forEach((p, i) => {
    const group = new THREE.Group()
    scene.add(group)
    groups.push(group)

    const tiltObj = new THREE.Object3D()
    tiltObj.rotation.copy(p.tilt)
    group.add(tiltObj)
    const body = new THREE.Mesh(
      track(new THREE.SphereGeometry(p.r, 40, 28)),
      track(new THREE.MeshStandardMaterial({
        map: track(planetTexture(p.hue, 100 + i)), roughness: 0.9, metalness: 0,
        emissive: new THREE.Color(p.hue), emissiveIntensity: 0.12,
      })),
    )
    tiltObj.add(body)
    bodies.push(body)
    // A soft rim of light round the edge, like an atmosphere.
    const [hr, hg, hb] = hexRGB(p.hue)
    tiltObj.add(new THREE.Mesh(
      track(new THREE.SphereGeometry(p.r * 1.004, 40, 28)),
      track(new THREE.ShaderMaterial({
        vertexShader: RIM_VERT, fragmentShader: RIM_FRAG, transparent: true, depthWrite: false,
        blending: THREE.AdditiveBlending,
        uniforms: { uColor: { value: new THREE.Vector3(hr * 0.6 + 0.4, hg * 0.6 + 0.4, hb * 0.6 + 0.4) } },
      })),
    ))

    const disc = new THREE.Mesh(
      track(new THREE.RingGeometry(p.ringIn - 0.5, p.ringOut + 0.6, 96, 1)),
      track(new THREE.MeshBasicMaterial({
        color: p.hue, transparent: true, opacity: 0.05, side: THREE.DoubleSide, depthWrite: false,
        blending: THREE.AdditiveBlending,
      })),
    )
    disc.rotation.x = -Math.PI / 2
    tiltObj.add(disc)

    const halo = new THREE.Sprite(track(new THREE.SpriteMaterial({
      map: glow, color: p.hue, transparent: true, opacity: 0.4, depthWrite: false, blending: THREE.AdditiveBlending,
    })))
    halo.scale.setScalar(p.r * 3.4)
    group.add(halo)

    // The words, one point cloud for the whole planet.
    const n = p.stars.length
    const pos = new Float32Array(n * 3)
    const col = new Float32Array(n * 3)
    const size = new Float32Array(n)
    const recent = new Float32Array(n)
    const phase = new Float32Array(n)
    p.stars.forEach((si, k) => {
      const s = sys.stars[si]!
      pos.set([s.local.x, s.local.y, s.local.z], k * 3)
      col.set(s.g ? GOLD : BLUE, k * 3)
      size[k] = s.g ? 0.95 : 0.58
      recent[k] = s.d !== null && s.d <= 7 ? 1 : 0
      phase[k] = (k * 1.7) % 6.28
    })
    const geo = track(new THREE.BufferGeometry())
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3))
    geo.setAttribute('aColor', new THREE.BufferAttribute(col, 3))
    geo.setAttribute('aSize', new THREE.BufferAttribute(size, 1))
    geo.setAttribute('aRecent', new THREE.BufferAttribute(recent, 1))
    geo.setAttribute('aPhase', new THREE.BufferAttribute(phase, 1))
    const cloud = new THREE.Points(geo, wordMat)
    cloud.frustumCulled = false
    group.add(cloud)

    // The orbit, a faint line in the planet's colour.
    const pts: THREE.Vector3[] = []
    for (let k = 0; k <= 160; k++) {
      const a = (k / 160) * Math.PI * 2
      pts.push(new THREE.Vector3(Math.cos(a) * p.orbit, 0, Math.sin(a) * p.orbit))
    }
    scene.add(new THREE.Line(
      track(new THREE.BufferGeometry().setFromPoints(pts)),
      track(new THREE.LineBasicMaterial({ color: p.hue, transparent: true, opacity: 0.28, depthWrite: false })),
    ))
  })

  // ------------------------------------------------ the chosen word
  const marker = new THREE.Sprite(track(new THREE.SpriteMaterial({
    map: track(ringTexture()), color: '#FFFFFF', transparent: true, depthTest: false, depthWrite: false,
  })))
  marker.renderOrder = 10
  marker.visible = false
  scene.add(marker)
  const MAX_LINKS = 12
  const linkPos = new Float32Array(MAX_LINKS * 2 * 3)
  const linkGeo = track(new THREE.BufferGeometry())
  linkGeo.setAttribute('position', new THREE.BufferAttribute(linkPos, 3))
  linkGeo.setDrawRange(0, 0)
  const links = new THREE.LineSegments(linkGeo, track(new THREE.LineBasicMaterial({
    color: '#FFFFFF', transparent: true, opacity: 0.5, depthWrite: false,
  })))
  links.frustumCulled = false
  scene.add(links)
  let selected: number | null = null
  let linkIds: number[] = []

  // ------------------------------------------------ sizes
  let W = 1
  let H = 1
  let pxScale = 1
  let overviewDist = 100
  const tanHalf = Math.tan(THREE.MathUtils.degToRad(FOV / 2))
  const labelSize = new Map<HTMLElement, [number, number]>()
  const fitOverview = () => {
    const aspect = W / H
    const E = sys.extent
    const byW = E / (tanHalf * aspect)
    const byH = (E * Math.sin(overviewEl(aspect)) * 1.15) / tanHalf
    let d = Math.max(byW, byH) * 1.06
    // A phone held upright is narrow: fitting the whole width would shrink
    // every planet to a dot. Let the outer orbits swing past the edges
    // instead; the chips at the bottom reach every planet anyway.
    if (aspect < 0.8) d *= 0.8
    return Math.max(d, SUN_R * 8)
  }
  const resize = () => {
    W = Math.max(1, mount.clientWidth)
    H = Math.max(1, mount.clientHeight)
    renderer.setSize(W, H, false)
    camera.aspect = W / H
    if (camera.view?.enabled) camera.setViewOffset(W, H, 0, camera.view.offsetY, W, H)
    camera.updateProjectionMatrix()
    pxScale = H / (2 * tanHalf)
    wordMat.uniforms.uScale!.value = pxScale * pr
    overviewDist = fitOverview()
    controls.maxDistance = overviewDist * 2.2
    labelSize.clear()
  }
  resize()
  const ro = new ResizeObserver(resize)
  ro.observe(mount)
  window.addEventListener('resize', resize)

  // ------------------------------------------------ flying
  type Flight = {
    t0: number; dur: number; fromPos: THREE.Vector3; fromTarget: THREE.Vector3
    anchor: number | null; offPos: THREE.Vector3; offTarget: THREE.Vector3; lift: number
  }
  let flight: Flight | null = null
  let focus: number | null = null
  const lastAnchor = new THREE.Vector3()
  const ORIGIN = new THREE.Vector3()
  const UP = new THREE.Vector3(0, 1, 0)
  const anchorPos = (a: number | null) => (a === null ? ORIGIN : groups[a]!.position)

  const setBounds = () => {
    const p = focus === null ? null : sys.planets[focus]!
    controls.minDistance = p ? p.r * 1.35 : SUN_R * 1.8
    controls.maxDistance = overviewDist * 2.2
  }
  const setFocus = (i: number | null) => {
    if (focus !== i) {
      focus = i
      handlers.current.focus(i)
    }
    lastAnchor.copy(anchorPos(i))
  }
  const fly = (anchor: number | null, offPos: THREE.Vector3, offTarget: THREE.Vector3, dur = 1.1) => {
    setFocus(anchor)
    const a = anchorPos(anchor)
    const end = a.clone().add(offPos)
    flight = {
      t0: performance.now(), dur: reduce ? 0 : dur * 1000,
      fromPos: camera.position.clone(), fromTarget: controls.target.clone(),
      anchor, offPos, offTarget,
      // Arc up and over on the way, rather than skimming through the sun.
      lift: Math.min(camera.position.distanceTo(end) * 0.22, overviewDist * 0.35),
    }
  }
  const fitDist = (rad: number) => (rad / tanHalf / Math.min(1, W / H)) * 1.05

  const api: SceneApi = {
    flyToPlanet(i) {
      const p = sys.planets[i]
      if (!p) return
      const pos = groups[i]!.position
      // Look from a little to the side of the sun, so the planet is mostly with a slice of night on one edge.
      // lit, with a slice of night on one edge, and from well above its
      // ring, so the words spread out instead of lining up edge-on.
      const side = pos.clone().negate().setY(0).normalize().applyAxisAngle(UP, 0.7)
      const normal = new THREE.Vector3(0, 1, 0).applyEuler(p.tilt)
      if (normal.dot(camera.position.clone().sub(pos)) < 0 && camera.position.y < 0) normal.negate()
      const el = 0.85
      const dir = side.multiplyScalar(Math.cos(el)).add(normal.multiplyScalar(Math.sin(el))).normalize()
      fly(i, dir.multiplyScalar(fitDist(p.ringOut * 1.3)), new THREE.Vector3())
    },
    flyHome() {
      const off = camera.position.clone().sub(controls.target)
      const az = Math.atan2(off.x, off.z)
      const el = overviewEl(W / H)
      const pos = new THREE.Vector3(
        Math.sin(az) * Math.cos(el), Math.sin(el), Math.cos(az) * Math.cos(el),
      ).multiplyScalar(overviewDist)
      fly(null, pos, new THREE.Vector3(), 1.3)
    },
    flyToStar(i, close) {
      const s = sys.stars[i]
      if (!s) return
      const p = sys.planets[s.planet]!
      if (close || focus !== s.planet) {
        // From above the ring and a little outside it, looking down at the
        // word with its planet to one side, not hiding behind it.
        const normal = new THREE.Vector3(0, 1, 0).applyEuler(p.tilt)
        const out = s.local.clone().projectOnPlane(normal).normalize()
        const dir = normal.multiplyScalar(1.5).add(out).normalize()
        const d = (12 + p.r * 2.2) / Math.sqrt(Math.min(1, W / H))
        fly(s.planet, s.local.clone().add(dir.multiplyScalar(d)), s.local.clone())
      } else {
        // Already here: slide sideways to centre the word, same distance.
        const keep = camera.position.clone().sub(controls.target)
        fly(s.planet, s.local.clone().add(keep), s.local.clone(), 0.7)
      }
    },
    select(i) {
      selected = i
      marker.visible = i !== null
      if (i === null) linkIds = []
    },
    setLinks(ids) {
      linkIds = ids.slice(0, MAX_LINKS)
    },
    dispose() {},
  }

  // ------------------------------------------------ picking
  const v = new THREE.Vector3()
  const w = new THREE.Vector3()
  /** Screen position of a world point, or null if behind the camera. */
  const screen = (p: THREE.Vector3): [number, number] | null => {
    v.copy(p).project(camera)
    if (v.z > 1 || v.z < -1) return null
    return [((v.x + 1) / 2) * W, ((1 - v.y) / 2) * H]
  }
  const projR = (p: THREE.Vector3, rad: number) => (rad * pxScale) / Math.max(0.001, camera.position.distanceTo(p))
  const starWorld = (i: number, out: THREE.Vector3) => {
    const s = sys.stars[i]!
    return out.copy(s.local).add(groups[s.planet]!.position)
  }
  /** Hidden behind its own planet? */
  const occluded = (i: number, sx: number, sy: number) => {
    const s = sys.stars[i]!
    const c = groups[s.planet]!.position
    const pc = screen(c)
    if (!pc) return false
    const behind = camera.position.distanceTo(w) > camera.position.distanceTo(c)
    return behind && Math.hypot(sx - pc[0], sy - pc[1]) < projR(c, sys.planets[s.planet]!.r) * 0.95
  }
  const nearestStar = (x: number, y: number, reach: number, only: number | null = null) => {
    let best = -1
    let bestD = reach * reach
    const list = only === null ? null : sys.planets[only]!.stars
    const count = list ? list.length : sys.stars.length
    for (let k = 0; k < count; k++) {
      const i = list ? list[k]! : k
      const sp = screen(starWorld(i, w))
      if (!sp) continue
      const d = (sp[0] - x) ** 2 + (sp[1] - y) ** 2
      if (d < bestD && !occluded(i, sp[0], sp[1])) { bestD = d; best = i }
    }
    return best
  }
  const pickAt = (x: number, y: number) => {
    let planet = -1
    let bestP = Infinity
    sys.planets.forEach((p, i) => {
      const c = groups[i]!.position
      const sp = screen(c)
      if (!sp) return
      const d = Math.hypot(sp[0] - x, sp[1] - y)
      // From afar, anywhere on a planet's ring counts as the planet: the
      // words are specks then, and a child tapping a ring means "go there".
      const reach = i === focus ? projR(c, p.r) + 6 : Math.max(projR(c, p.ringOut) * 0.9, projR(c, p.r) + 14, 26)
      if (d < reach && d < bestP) { bestP = d; planet = i }
    })
    const ss = screen(ORIGIN)
    const sunHit = ss ? Math.hypot(ss[0] - x, ss[1] - y) < Math.max(projR(ORIGIN, SUN_R) + 10, 30) : false
    const star = nearestStar(x, y, REACH)
    return { planet, star, sunHit }
  }

  let pointers = 0
  let down: { x: number; y: number; t: number; multi: boolean } | null = null
  const local = (e: PointerEvent): [number, number] => {
    const r = canvas.getBoundingClientRect()
    return [e.clientX - r.left, e.clientY - r.top]
  }
  const onDown = (e: PointerEvent) => {
    pointers++
    if (pointers > 1 && down) down.multi = true
    else down = { x: e.clientX, y: e.clientY, t: performance.now(), multi: false }
    // Grabbing the sky mid-flight stops the flight where it is.
    if (flight) { flight = null; setBounds() }
    canvas.style.cursor = 'grabbing'
  }
  const onUp = (e: PointerEvent) => {
    pointers = Math.max(0, pointers - 1)
    canvas.style.cursor = 'grab'
    if (!down) return
    const moved = Math.hypot(e.clientX - down.x, e.clientY - down.y)
    const tap = !down.multi && moved < 10 && performance.now() - down.t < 650
    if (pointers === 0) down = null
    if (!tap) { handlers.current.acted(); return }
    const [x, y] = local(e)
    const { planet, star, sunHit } = pickAt(x, y)
    const starPlanet = star >= 0 ? sys.stars[star]!.planet : -1
    if (star >= 0 && focus !== null && starPlanet === focus) {
      api.flyToStar(star, false)
      handlers.current.tapStar(star)
    } else if (planet >= 0) {
      api.flyToPlanet(planet)
      handlers.current.tapPlanet(planet)
    } else if (star >= 0) {
      api.flyToStar(star, true)
      handlers.current.tapStar(star)
    } else if (sunHit) {
      api.flyHome()
      handlers.current.tapSun()
    } else {
      handlers.current.tapEmpty()
    }
  }
  const onCancel = () => { pointers = Math.max(0, pointers - 1); down = null }
  let hoverAt: [number, number] | null = null
  let hoverStar = -1
  const onMove = (e: PointerEvent) => {
    if (e.pointerType !== 'mouse' || e.buttons !== 0) { hoverAt = null; return }
    hoverAt = local(e)
  }
  const onLeave = () => { hoverAt = null; hoverStar = -1 }
  const onWheel = () => {
    if (flight) { flight = null; setBounds() }
    handlers.current.acted()
  }
  canvas.addEventListener('pointerdown', onDown)
  canvas.addEventListener('pointerup', onUp)
  canvas.addEventListener('pointercancel', onCancel)
  canvas.addEventListener('pointermove', onMove)
  canvas.addEventListener('pointerleave', onLeave)
  canvas.addEventListener('wheel', onWheel, { passive: true })

  // ------------------------------------------------ labels
  type Rect = [number, number, number, number]
  const overlaps = (a: Rect, list: Rect[]) =>
    list.some((b) => a[0] < b[0] + b[2] && a[0] + a[2] > b[0] && a[1] < b[1] + b[3] && a[1] + a[3] > b[1])
  const sizeOf = (el: HTMLElement): [number, number] => {
    let s = labelSize.get(el)
    if (!s) { s = [el.offsetWidth, el.offsetHeight]; labelSize.set(el, s) }
    return s
  }
  const show = (el: HTMLElement, x: number, y: number, opacity = 1) => {
    el.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`
    el.style.visibility = 'visible'
    el.style.opacity = String(opacity)
  }
  const hide = (el: HTMLElement | null | undefined) => {
    if (el) el.style.visibility = 'hidden'
  }
  let assigned: number[] = []
  let pickedText = ''
  let frameNo = 0
  const placeLabels = () => {
    const L = labels.current
    const placed: Rect[] = []
    // Sizes are measured once and remembered; forget them now and then, in
    // case a font arrived late or a bar grew.
    if (frameNo % 60 === 1) labelSize.clear()
    if (L.picked && L.picked.textContent !== pickedText) {
      pickedText = L.picked.textContent ?? ''
      labelSize.delete(L.picked)
    }
    for (const el of L.reserved) {
      if (el) {
        const [rw, rh] = sizeOf(el)
        placed.push([el.offsetLeft, el.offsetTop, rw, rh])
      }
    }
    // The chosen word's tag goes first: it matters most.
    if (selected !== null && L.picked) {
      const sp = screen(starWorld(selected, w))
      if (sp) {
        const [lw, lh] = sizeOf(L.picked)
        const rect: Rect = [sp[0] - lw / 2, sp[1] - lh - 20, lw, lh]
        show(L.picked, rect[0], rect[1])
        placed.push(rect, [sp[0] - 20, sp[1] - 20, 40, 40])
      } else hide(L.picked)
    } else hide(L.picked)

    // Planet and sun names: nearest first, and a name that would sit on top
    // of another is left out rather than piled up.
    const order = sys.planets.map((_, i) => i)
      .sort((a, b) => camera.position.distanceTo(groups[a]!.position) - camera.position.distanceTo(groups[b]!.position))
    const items: [HTMLElement | null | undefined, THREE.Vector3, number, boolean][] = [
      [L.sun, ORIGIN, SUN_R, true],
      ...order.map((i): [HTMLElement | null | undefined, THREE.Vector3, number, boolean] => {
        const p = sys.planets[i]!
        const c = groups[i]!.position
        // Close up, the planet you are visiting is named in the bar below,
        // and its words get the space instead.
        const near = i === focus && projR(c, p.ringOut) > Math.min(W, H) * 0.3
        return [L.planets[i], c, p.r, !near]
      }),
    ]
    for (const [el, c, rad, want] of items) {
      if (!el) continue
      const sp = want ? screen(c) : null
      if (!sp) { hide(el); el.style.pointerEvents = 'none'; continue }
      const [lw, lh] = sizeOf(el)
      // A planet near the edge keeps its whole name on screen.
      const rect: Rect = [Math.min(Math.max(4, sp[0] - lw / 2), W - lw - 4), sp[1] + projR(c, rad) + 8, lw, lh]
      const off = sp[0] < 0 || sp[0] > W || rect[1] < -lh || rect[1] > H
      if (off || overlaps(rect, placed)) { hide(el); el.style.pointerEvents = 'none'; continue }
      show(el, rect[0], rect[1])
      el.style.pointerEvents = 'auto'
      placed.push(rect)
    }

    // Close to a planet, the nearest few words show their names.
    const p = focus === null ? null : sys.planets[focus]!
    // "Close" is measured on screen, so it means the same on a phone and a laptop.
    const close = p !== null && focus !== null && projR(groups[focus]!.position, p.ringOut) > Math.min(W, H) * 0.3
    if (frameNo % 8 === 0) {
      assigned = []
      if (close && p) {
        const cand: [number, number, number, number][] = []
        for (const i of p.stars) {
          if (i === selected) continue
          const sp = screen(starWorld(i, w))
          if (!sp || sp[0] < 20 || sp[0] > W - 20 || sp[1] < 30 || sp[1] > H - 10) continue
          if (occluded(i, sp[0], sp[1])) continue
          cand.push([i, camera.position.distanceTo(w), sp[0], sp[1]])
        }
        cand.sort((a, b) => a[1] - b[1])
        const taken = [...placed]
        for (const [i, , x, y] of cand) {
          if (assigned.length >= WORD_LABELS) break
          const word = sys.stars[i]!.w
          const lw = word.length * 7.6 + 22
          const rect: Rect = [x - lw / 2 - 4, y - 34, lw + 8, 30]
          if (overlaps(rect, taken)) continue
          taken.push(rect)
          assigned.push(i)
        }
      }
    }
    L.words.forEach((el, k) => {
      if (!el) return
      const i = close ? assigned[k] : undefined
      if (i === undefined) { hide(el); slots.current[k] = null; return }
      if (slots.current[k] !== i) {
        const s = sys.stars[i]!
        slots.current[k] = i
        el.textContent = s.w
        el.style.color = s.g ? '#FFE29A' : '#DCE9FF'
        labelSize.delete(el)
      }
      const sp = screen(starWorld(i, w))
      if (!sp) { hide(el); return }
      const [lw, lh] = sizeOf(el)
      show(el, sp[0] - lw / 2, sp[1] - lh - 9)
    })

    // A mouse resting over a star names it.
    if (L.hover) {
      if (hoverAt && frameNo % 3 === 0) {
        const near = nearestStar(hoverAt[0], hoverAt[1], 16)
        hoverStar = near
        const pick = pickAt(hoverAt[0], hoverAt[1])
        canvas.style.cursor = near >= 0 || pick.planet >= 0 || pick.sunHit ? 'pointer' : 'grab'
      }
      if (!hoverAt) hoverStar = -1
      const sp = hoverStar >= 0 && hoverStar !== selected && !assigned.includes(hoverStar)
        ? screen(starWorld(hoverStar, w)) : null
      if (sp) {
        const s = sys.stars[hoverStar]!
        if (L.hover.textContent !== s.w) {
          L.hover.textContent = s.w
          L.hover.style.color = s.g ? '#FFE29A' : '#DCE9FF'
          labelSize.delete(L.hover)
        }
        const [lw, lh] = sizeOf(L.hover)
        show(L.hover, sp[0] - lw / 2, sp[1] - lh - 9)
      } else hide(L.hover)
    }
  }

  // ------------------------------------------------ the loop
  // Start far out and swoop in: the first second should feel like arriving.
  const el0 = overviewEl(W / H)
  const startDir = new THREE.Vector3(Math.sin(0.6) * Math.cos(el0), Math.sin(el0), Math.cos(0.6) * Math.cos(el0))
  camera.position.copy(startDir).multiplyScalar(overviewDist * (reduce ? 1 : 2.6))
  camera.lookAt(ORIGIN)
  setBounds()
  fly(null, startDir.clone().multiplyScalar(overviewDist), new THREE.Vector3(), 1.8)

  let raf = 0
  let shift = 0
  const scratch = new THREE.Vector3()
  const scratch2 = new THREE.Vector3()
  let last = performance.now()
  let t = 0
  const placeBodies = () => {
    sys.planets.forEach((p, i) => {
      const a = p.phase + t * p.speed
      groups[i]!.position.set(Math.cos(a) * p.orbit, 0, -Math.sin(a) * p.orbit)
      bodies[i]!.rotation.y = t * p.spin + i
    })
  }
  const frame = (now: number) => {
    raf = requestAnimationFrame(frame)
    const dt = Math.min(0.05, (now - last) / 1000)
    last = now
    if (!reduce) t += dt
    frameNo++

    placeBodies()
    sun.rotation.y = t * 0.04
    corona[1]?.scale.setScalar(SUN_R * (11 + Math.sin(t * 0.8) * 0.6))

    // Ride along with the planet being visited, so it stays put on screen
    // while it orbits, and spinning round it feels like turning a globe.
    if (focus !== null && !flight) {
      const d = scratch.copy(groups[focus]!.position).sub(lastAnchor)
      camera.position.add(d)
      controls.target.add(d)
    }
    if (focus !== null) lastAnchor.copy(groups[focus]!.position)

    if (flight) {
      const k = flight.dur ? Math.min(1, (now - flight.t0) / flight.dur) : 1
      const e = ease(k)
      const a = anchorPos(flight.anchor)
      const endPos = scratch.copy(a).add(flight.offPos)
      const endTarget = scratch2.copy(a).add(flight.offTarget)
      camera.position.lerpVectors(flight.fromPos, endPos, e)
      camera.position.y += Math.sin(Math.PI * e) * flight.lift
      controls.target.lerpVectors(flight.fromTarget, endTarget, e)
      camera.lookAt(controls.target)
      if (k >= 1) { flight = null; setBounds(); controls.update() }
    } else {
      controls.update()
    }

    sky.position.copy(camera.position)
    wordMat.uniforms.uTime!.value = t
    ;((sky.children[0] as THREE.Points).material as THREE.ShaderMaterial).uniforms.uTime!.value = t

    if (selected !== null) {
      starWorld(selected, marker.position)
      const dist = camera.position.distanceTo(marker.position)
      marker.scale.setScalar((34 * dist) / pxScale)
      ;(marker.material as THREE.SpriteMaterial).rotation = reduce ? 0 : t * 0.6
      const from = marker.position
      let n = 0
      for (const id of linkIds) {
        starWorld(id, w)
        linkPos.set([from.x, from.y, from.z, w.x, w.y, w.z], n * 6)
        n++
      }
      linkGeo.setDrawRange(0, n * 2)
      ;(linkGeo.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true
    } else {
      linkGeo.setDrawRange(0, 0)
    }

    // Centre the picture in the clear space between the bars, so a chosen
    // star sits above its card rather than underneath it.
    const [topBar, bottomBar] = labels.current.reserved
    const wantShift = topBar && bottomBar
      ? Math.round(Math.max(-H * 0.2, Math.min(H * 0.2, (sizeOf(bottomBar)[1] - sizeOf(topBar)[1]) / 2)))
      : 0
    if (Math.abs(wantShift - shift) > 0.5) {
      shift += (wantShift - shift) * (reduce ? 1 : 0.12)
      camera.setViewOffset(W, H, 0, shift, W, H)
    } else if (shift !== wantShift) {
      shift = wantShift
      if (shift) camera.setViewOffset(W, H, 0, shift, W, H)
      else camera.clearViewOffset()
    }

    renderer.render(scene, camera)
    placeLabels()
  }
  raf = requestAnimationFrame(frame)

  api.dispose = () => {
    cancelAnimationFrame(raf)
    ro.disconnect()
    window.removeEventListener('resize', resize)
    canvas.removeEventListener('pointerdown', onDown)
    canvas.removeEventListener('pointerup', onUp)
    canvas.removeEventListener('pointercancel', onCancel)
    canvas.removeEventListener('pointermove', onMove)
    canvas.removeEventListener('pointerleave', onLeave)
    canvas.removeEventListener('wheel', onWheel)
    controls.dispose()
    for (const d of disposables) d.dispose()
    renderer.dispose()
    renderer.forceContextLoss()
    canvas.remove()
  }
  return api
}

/** Can this browser draw WebGL at all? Asked once, before building anything. */
function hasWebGL(): boolean {
  try {
    const c = document.createElement('canvas')
    return Boolean(c.getContext('webgl2') || c.getContext('webgl'))
  } catch {
    return false
  }
}

// ------------------------------------------------------------------ screen

const glass: CSSProperties = {
  background: 'rgba(16,18,48,0.62)',
  border: '1px solid rgba(255,255,255,0.18)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
  color: '#FFFFFF',
}
const pillButton: CSSProperties = {
  ...glass,
  minHeight: TAP_MIN, borderRadius: radius.pill, padding: '0 16px', fontSize: 16, fontWeight: 700,
  fontFamily: font.body, cursor: 'pointer', whiteSpace: 'nowrap', pointerEvents: 'auto',
  display: 'inline-flex', alignItems: 'center', gap: 6,
}
const tag: CSSProperties = {
  position: 'absolute', left: 0, top: 0, visibility: 'hidden', whiteSpace: 'nowrap',
  borderRadius: radius.pill, fontFamily: font.body, transition: 'opacity 0.2s',
}
const SPACE = 'radial-gradient(ellipse at 30% 20%, #2B1B4F 0%, #121734 55%, #070A1C 100%)'

export function WordUniverse3D({
  learnerId, onHome, onFlat,
}: { learnerId: string; onHome: () => void; onFlat: () => void }) {
  ensureStyle()
  const [data, setData] = useState<Universe | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [noGL, setNoGL] = useState(false)
  const [focus, setFocus] = useState<number | null>(null)
  const [picked, setPicked] = useState<number | null>(null)
  const [info, setInfo] = useState<{ definition: string | null; neighbours: string[] } | null>(null)
  const [query, setQuery] = useState('')
  const [acted, setActed] = useState(false)
  const mountRef = useRef<HTMLDivElement | null>(null)
  const sceneRef = useRef<SceneApi | null>(null)
  const labels = useRef<Labels>({ planets: [], sun: null, words: [], picked: null, hover: null, reserved: [] })
  const slots = useRef<(number | null)[]>(Array.from({ length: WORD_LABELS }, () => null))
  const asked = useRef(0)

  const load = () => {
    setError(null)
    api.universe(learnerId).then(setData).catch(setError)
  }
  useEffect(load, [learnerId])

  const sys = useMemo(() => (data ? layout(data) : null), [data])
  const byWord = useMemo(() => {
    const m = new Map<string, number>()
    sys?.stars.forEach((s, i) => m.set(s.w.toLowerCase(), i))
    return m
  }, [sys])

  const pick = (i: number | null) => {
    setPicked(i)
    setInfo(null)
    sceneRef.current?.select(i)
    if (i === null || !sys) return
    const star = sys.stars[i]!
    sfx.tap()
    say(star.w)
    const ask = ++asked.current
    api.wordMap(learnerId, star.w, null, 12)
      .then((map) => {
        if (ask !== asked.current) return
        const me = map.nodes.find((n) => n.word.toLowerCase() === star.w.toLowerCase())
        const id = me?.id
        const neighbours = map.edges
          .filter((e) => e.a === id || e.b === id)
          .map((e) => (e.a === id ? e.b : e.a))
          .map((nid) => map.nodes.find((n) => n.id === nid)?.word)
          .filter((w): w is string => Boolean(w))
        setInfo({ definition: me?.definition ?? null, neighbours })
      })
      .catch(() => { if (ask === asked.current) setInfo({ definition: null, neighbours: [] }) })
  }

  // The scene calls back through this, so it always reaches the latest render.
  const handlers = useRef<Handlers>({
    tapStar: () => {}, tapPlanet: () => {}, tapSun: () => {}, tapEmpty: () => {}, focus: () => {}, acted: () => {},
  })
  handlers.current = {
    tapStar: (i) => pick(i),
    tapPlanet: () => { sfx.tap(); pick(null) },
    tapSun: () => { sfx.tap(); pick(null) },
    tapEmpty: () => pick(null),
    focus: (i) => setFocus(i),
    acted: () => setActed(true),
  }

  useEffect(() => {
    const mount = mountRef.current
    if (!sys || !mount) return
    if (!hasWebGL()) { setNoGL(true); return }
    const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    let scene: SceneApi
    try {
      scene = buildScene(mount, sys, labels, slots, handlers, reduce)
    } catch {
      setNoGL(true)
      return
    }
    sceneRef.current = scene
    return () => {
      sceneRef.current = null
      scene.dispose()
    }
  }, [sys])

  // Friends of the chosen word, drawn as lines to them.
  const friends = useMemo(() => {
    if (!info) return []
    return info.neighbours
      .map((w) => byWord.get(w.toLowerCase()))
      .filter((i): i is number => i !== undefined && i !== picked)
  }, [info, byWord, picked])
  useEffect(() => { sceneRef.current?.setLinks(friends) }, [friends])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (picked !== null) pick(null)
      else if (focus !== null) sceneRef.current?.flyHome()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q || !sys) return []
    const exact: number[] = []
    const starts: number[] = []
    const inside: number[] = []
    sys.stars.forEach((s, i) => {
      const w = s.w.toLowerCase()
      if (w === q) exact.push(i)
      else if (w.startsWith(q)) starts.push(i)
      else if (q.length > 2 && w.includes(q)) inside.push(i)
    })
    const byLen = (a: number, b: number) => sys.stars[a]!.w.length - sys.stars[b]!.w.length
    return [...exact, ...starts.sort(byLen), ...inside.sort(byLen)].slice(0, 6)
  }, [query, sys])

  const goToStar = (i: number) => {
    setQuery('')
    ;(document.activeElement as HTMLElement | null)?.blur?.()
    sceneRef.current?.flyToStar(i, true)
    pick(i)
  }
  const goToPlanet = (i: number) => {
    sfx.tap()
    pick(null)
    sceneRef.current?.flyToPlanet(i)
  }
  const goHome = () => {
    pick(null)
    sceneRef.current?.flyHome()
  }

  if (error) return <ErrorScreen error={error} onRetry={load} onHome={onHome} />
  if (!data || !sys) return <Loading what="Lighting up the stars" />

  const star = picked === null ? null : sys.stars[picked] ?? null
  const planet = focus === null ? null : sys.planets[focus] ?? null

  if (noGL) {
    return (
      <div style={{ minHeight: '100dvh', background: SPACE, color: '#FFFFFF', display: 'grid', placeItems: 'center', padding: 24, boxSizing: 'border-box' }}>
        <div style={{ maxWidth: 420, textAlign: 'center' }}>
          <div style={{ display: 'grid', placeItems: 'center' }}><Mascot size={96} mood="wonder" label="" /></div>
          <h1 style={{ fontFamily: font.display, fontSize: 28, margin: '12px 0 8px' }}>The 3D universe can't open here</h1>
          <p style={{ fontSize: 17, lineHeight: 1.5, color: '#DCD8FF', margin: '0 0 20px' }}>
            This device can't draw the planets. The flat map has all the same stars.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button type="button" onClick={onFlat} style={{ ...pillButton, background: '#FFB347', color: '#2B1B00', border: 'none' }}>
              Show the flat map
            </button>
            <button type="button" onClick={onHome} style={pillButton}>← Home</button>
          </div>
        </div>
      </div>
    )
  }

  const hint = !acted && picked === null && focus === null
  const coarse = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches

  return (
    <div style={{
      position: 'relative', height: '100dvh', width: '100%', overflow: 'hidden', background: SPACE,
      color: '#FFFFFF', fontFamily: font.body, userSelect: 'none', WebkitUserSelect: 'none',
    }}>
      {/* the canvas goes in here */}
      <div ref={mountRef} style={{ position: 'absolute', inset: 0 }}>
        {/* names over the 3D picture, placed every frame */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
          <button
            ref={(el) => { labels.current.sun = el }}
            type="button" onClick={goHome}
            style={{ ...tag, padding: '4px 12px', background: 'rgba(60,30,0,0.45)', border: '1px solid rgba(255,214,120,0.6)',
                     color: '#FFE7A8', fontSize: 15, fontWeight: 700, fontFamily: font.display, cursor: 'pointer' }}
          >
            Your words · {data.total.toLocaleString()}
          </button>
          {sys.planets.map((p, i) => (
            <button
              key={`p${i}`}
              ref={(el) => { labels.current.planets[i] = el }}
              type="button" onClick={() => goToPlanet(i)}
              style={{ ...tag, padding: '4px 12px', background: 'rgba(12,14,40,0.6)', border: `1.5px solid ${p.hue}`,
                       color: '#FFFFFF', cursor: 'pointer', textAlign: 'center', lineHeight: 1.15 }}
            >
              <span style={{ display: 'block', fontFamily: font.display, fontWeight: 600, fontSize: 15 }}>{p.name}</span>
              <span style={{ display: 'block', fontSize: 12, color: '#C9C3F0' }}>{p.count} {p.count === 1 ? 'word' : 'words'}</span>
            </button>
          ))}
          {Array.from({ length: WORD_LABELS }, (_, k) => (
            <button
              key={`w${k}`}
              ref={(el) => { labels.current.words[k] = el }}
              type="button" tabIndex={-1}
              onClick={() => { const i = slots.current[k]; if (i !== null && i !== undefined) goToStar(i) }}
              style={{ ...tag, padding: '3px 10px', background: 'rgba(10,12,36,0.55)', border: 'none',
                       fontSize: 14, fontWeight: 700, cursor: 'pointer', pointerEvents: 'auto' }}
            />
          ))}
          <div
            ref={(el) => { labels.current.hover = el }}
            style={{ ...tag, padding: '3px 10px', background: 'rgba(10,12,36,0.7)', fontSize: 14, fontWeight: 700 }}
          />
          <div
            ref={(el) => { labels.current.picked = el }}
            style={{ ...tag, padding: '5px 14px', fontSize: 19, fontWeight: 600, fontFamily: font.display,
                     background: star?.g ? 'rgba(255,201,85,0.92)' : 'rgba(169,204,255,0.92)', color: '#1A1440',
                     boxShadow: '0 4px 14px rgba(0,0,0,0.35)' }}
          >
            {star?.w ?? ''}
          </div>
        </div>
      </div>

      {/* --------------------------------------------- top bar */}
      <div ref={(el) => { labels.current.reserved[0] = el }} style={{
        position: 'absolute', top: 0, left: 0, right: 0, pointerEvents: 'none',
        padding: 'max(10px, env(safe-area-inset-top, 0px)) 12px 0',
        background: 'linear-gradient(rgba(7,10,28,0.7), rgba(7,10,28,0))',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: 980, margin: '0 auto' }}>
          <ExitButton onExit={onHome} />
          <div style={{ width: 96, flexShrink: 0 }} aria-hidden />
          <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
            <h1 style={{ margin: 0, fontFamily: font.display, fontWeight: 600, fontSize: 'clamp(19px, 4.6vw, 26px)', lineHeight: 1.1,
                         textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
              Word universe
            </h1>
            <div style={{ fontSize: 13, color: '#D9D3FF', marginTop: 2, pointerEvents: 'auto' }}>
              <Trace id="calc.counted from T7 rows">{data.total.toLocaleString()} stars</Trace>
              {' · '}
              <Trace id="calc.counted from T7.grown_at">
                <span style={{ color: '#FFD978' }}>{data.grown.toLocaleString()} shining bright</span>
              </Trace>
            </div>
          </div>
          <button type="button" onClick={onFlat} style={pillButton} aria-label="Switch to the flat map">
            <span aria-hidden="true">🗺️</span> Flat map
          </button>
        </div>

        <div style={{ position: 'relative', maxWidth: 420, margin: '10px auto 0', pointerEvents: 'auto' }}>
          <span aria-hidden="true" style={{ position: 'absolute', left: 15, top: 11, fontSize: 17, opacity: 0.8 }}>🔍</span>
          <input
            className="ws3-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && matches[0] !== undefined) goToStar(matches[0])
              if (e.key === 'Escape') setQuery('')
            }}
            placeholder="Find a star — type a word"
            aria-label="Find a word in your universe"
            autoComplete="off" spellCheck={false}
            style={{
              ...glass, width: '100%', boxSizing: 'border-box', minHeight: TAP_MIN, borderRadius: radius.pill,
              padding: '0 16px 0 42px', fontSize: 16, fontFamily: font.body, outline: 'none',
            }}
          />
          {query.trim() ? (
            <div role="listbox" style={{
              ...glass, position: 'absolute', left: 0, right: 0, top: TAP_MIN + 6, borderRadius: radius.xl,
              background: 'rgba(16,18,48,0.92)', padding: 6, zIndex: 5,
            }}>
              {matches.length ? matches.map((i) => {
                const s = sys.stars[i]!
                const p = sys.planets[s.planet]!
                return (
                  <button key={i} type="button" role="option" aria-selected={false} onClick={() => goToStar(i)} style={{
                    display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: TAP_MIN, padding: '0 12px',
                    background: 'transparent', border: 'none', color: '#FFFFFF', cursor: 'pointer', borderRadius: radius.lg,
                    fontSize: 17, fontFamily: font.body, textAlign: 'left',
                  }}>
                    <span aria-hidden="true" style={{ color: s.g ? '#FFC955' : '#A9CCFF', fontSize: 15 }}>{s.g ? '★' : '✦'}</span>
                    <span style={{ fontWeight: 700, flex: 1 }}>{s.w}</span>
                    <span style={{ fontSize: 13, color: '#BDB6EA' }}>
                      <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 9, background: p.hue, marginRight: 6 }} />
                      {p.name}
                    </span>
                  </button>
                )
              }) : (
                <div style={{ padding: '10px 12px', fontSize: 15, color: '#CFC9F5' }}>
                  No star called “{query.trim()}” yet. Keep playing and it might appear!
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {data.total === 0 ? (
        <div style={{ position: 'absolute', left: 16, right: 16, top: '38%', textAlign: 'center', pointerEvents: 'none' }}>
          <p style={{ ...glass, display: 'inline-block', padding: '12px 18px', borderRadius: radius.xl, fontSize: 17, margin: 0 }}>
            Your universe is waiting. Every word you play lights a new star!
          </p>
        </div>
      ) : null}

      {/* --------------------------------------------- bottom */}
      <div ref={(el) => { labels.current.reserved[1] = el }} style={{
        position: 'absolute', left: 0, right: 0, bottom: 0, pointerEvents: 'none',
        padding: '0 0 max(12px, env(safe-area-inset-bottom, 0px))',
        background: 'linear-gradient(rgba(7,10,28,0), rgba(7,10,28,0.65))',
      }}>
        {star ? (
          <div className="ws3-in" role="status" style={{
            ...glass, background: 'rgba(16,18,48,0.88)', pointerEvents: 'auto', maxWidth: 560, margin: '0 12px', marginInline: 'auto',
            borderRadius: radius.xl, padding: 16, boxSizing: 'border-box', width: 'calc(100% - 24px)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: font.display, fontSize: 30, fontWeight: 600, color: star.g ? '#FFD978' : '#DCE9FF' }}>
                {star.w}
              </span>
              <button type="button" onClick={() => say(star.w)} aria-label={`Say ${star.w}`} style={{
                ...pillButton, width: TAP_MIN, padding: 0, justifyContent: 'center', fontSize: 19,
              }}>🔊</button>
              <span style={{
                fontSize: 14, fontWeight: 700, borderRadius: radius.pill, padding: '4px 10px',
                background: star.g ? '#FFF4D6' : '#DCEBFF', color: star.g ? '#8A5A00' : '#1D4E89',
              }}>
                {star.g ? '⭐ Fully grown' : '🌱 Still growing'}
              </span>
              <button type="button" onClick={() => pick(null)} aria-label="Close" style={{
                ...pillButton, width: TAP_MIN, padding: 0, justifyContent: 'center', marginLeft: 'auto', fontSize: 20,
              }}>×</button>
            </div>
            <p style={{ margin: '8px 0 4px', fontSize: 17, color: '#EEEAFF', lineHeight: 1.5 }}>
              {info ? (info.definition ?? 'No meaning saved for this one yet.') : 'Looking it up…'}
            </p>
            <p style={{ margin: 0, fontSize: 14, color: '#B9B2E6' }}>
              {star.d === null ? 'Not met in practice yet.'
                : star.d === 0 ? 'Met today.'
                : `Last met ${star.d} day${star.d === 1 ? '' : 's'} ago.`}
              {' '}On planet <b style={{ color: sys.planets[star.planet]!.hue }}>{sys.planets[star.planet]!.name}</b>.
            </p>
            {friends.length ? (
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 14, color: '#B9B2E6' }}>Joined to:</span>
                {friends.slice(0, 6).map((i) => (
                  <button key={i} type="button" onClick={() => goToStar(i)} style={{
                    ...pillButton, minHeight: 38, padding: '0 12px', fontSize: 15,
                    color: sys.stars[i]!.g ? '#FFD978' : '#DCE9FF',
                  }}>{sys.stars[i]!.w}</button>
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          <>
            {hint ? (
              <p style={{ textAlign: 'center', fontSize: 14, color: '#CFC9F5', margin: '0 16px 10px', textShadow: '0 1px 4px #000' }}>
                Drag to spin · {coarse ? 'pinch' : 'scroll'} to zoom · tap a planet
              </p>
            ) : null}
            {planet ? (
              <div className="ws3-in" style={{ display: 'flex', justifyContent: 'center', margin: '0 12px 10px' }}>
                <div style={{
                  ...glass, pointerEvents: 'auto', borderRadius: radius.pill, padding: '8px 18px',
                  display: 'flex', alignItems: 'center', gap: 12, maxWidth: '100%', boxSizing: 'border-box',
                }}>
                  <span style={{ width: 14, height: 14, borderRadius: 14, background: planet.hue, boxShadow: `0 0 10px ${planet.hue}`, flex: 'none' }} />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ fontFamily: font.display, fontWeight: 600, fontSize: 18 }}>{planet.name}</span>
                    <span style={{ fontSize: 14, color: '#D9D3FF' }}>
                      {' · '}<Trace id="calc.counted from T7 rows">{planet.count} words</Trace>
                      {' · '}<Trace id="calc.counted from T7.grown_at"><span style={{ color: '#FFD978' }}>{planet.grown} shining</span></Trace>
                    </span>
                  </span>
                </div>
              </div>
            ) : null}
            <div className="ws3-scroll" style={{
              display: 'flex', gap: 8, overflowX: 'auto', padding: '2px 12px', pointerEvents: 'auto',
              justifyContent: 'safe center' as CSSProperties['justifyContent'],
            }}>
              <button type="button" onClick={goHome} aria-pressed={focus === null} style={{
                ...pillButton, flex: 'none',
                // At the whole system it just says where you are; away from
                // it, it is the bright way home.
                ...(focus === null
                  ? { background: 'rgba(255,179,71,0.25)', border: '2px solid #FFB347' }
                  : { background: '#FFB347', color: '#2B1B00', border: '2px solid #FFB347' }),
              }}>
                ☀️ Whole system
              </button>
              {sys.planets.map((p, i) => (
                <button key={i} type="button" onClick={() => goToPlanet(i)} aria-pressed={focus === i} style={{
                  ...pillButton, flex: 'none', fontSize: 15,
                  ...(focus === i ? { background: `${p.hue}55`, border: `2px solid ${p.hue}` } : {}),
                }}>
                  <span aria-hidden="true" style={{ width: 12, height: 12, borderRadius: 12, background: p.hue, boxShadow: `0 0 8px ${p.hue}` }} />
                  {p.name}
                  <span style={{ fontSize: 13, color: '#C9C3F0', fontWeight: 600 }}>{p.count}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
